import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import { EINRICHTUNGSTYPEN, BUNDESLAENDER } from '../../lib/constants'
import type { Arbeitgeber } from '../../lib/types'
import './AdminPages.css'

export default function ArbeitgeberListe() {
  const [arbeitgeber, setArbeitgeber] = useState<Arbeitgeber[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [showForm, setShowForm] = useState(false)
  const [formData, setFormData] = useState({ name: '', einrichtungstyp: '', traeger: '', website: '' })
  const [saving, setSaving] = useState(false)
  const navigate = useNavigate()

  useEffect(() => { loadData() }, [])

  async function loadData() {
    setLoading(true)
    const { data } = await supabase.from('arbeitgeber').select('*').order('name')
    setArbeitgeber((data as Arbeitgeber[]) || [])
    setLoading(false)
  }

  async function handleCreate() {
    if (!formData.name) return
    setSaving(true)
    const { error } = await supabase.from('arbeitgeber').insert(formData)
    if (!error) {
      setShowForm(false)
      setFormData({ name: '', einrichtungstyp: '', traeger: '', website: '' })
      loadData()
    }
    setSaving(false)
  }

  const filtered = arbeitgeber.filter((a) => {
    if (!search) return true
    const s = search.toLowerCase()
    return a.name?.toLowerCase().includes(s) || a.traeger?.toLowerCase().includes(s)
  })

  return (
    <div className="admin-page animate-fade-in-up">
      <div className="page-header">
        <div>
          <h1>Arbeitgeber</h1>
          <p className="page-subtitle">{arbeitgeber.length} Einrichtungen</p>
        </div>
        <button className="btn btn--primary" onClick={() => setShowForm(!showForm)}>
          {showForm ? '✕ Abbrechen' : '+ Neuer Arbeitgeber'}
        </button>
      </div>

      {showForm && (
        <div className="detail-section animate-fade-in-up" style={{ marginBottom: 'var(--space-6)' }}>
          <h3>Neuen Arbeitgeber anlegen</h3>
          <div className="form-grid">
            <div className="form-field">
              <label>Name *</label>
              <input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="Name der Einrichtung" id="ag-name" />
            </div>
            <div className="form-field">
              <label>Einrichtungstyp</label>
              <select value={formData.einrichtungstyp} onChange={(e) => setFormData({ ...formData, einrichtungstyp: e.target.value })} id="ag-typ">
                <option value="">Auswählen...</option>
                {EINRICHTUNGSTYPEN.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div className="form-field">
              <label>Träger</label>
              <input value={formData.traeger} onChange={(e) => setFormData({ ...formData, traeger: e.target.value })} placeholder="z.B. AWO, Caritas..." id="ag-traeger" />
            </div>
            <div className="form-field">
              <label>Website</label>
              <input value={formData.website} onChange={(e) => setFormData({ ...formData, website: e.target.value })} placeholder="https://..." id="ag-website" />
            </div>
          </div>
          <div style={{ marginTop: 'var(--space-4)' }}>
            <button className="btn btn--primary" onClick={handleCreate} disabled={saving || !formData.name}>
              {saving ? 'Speichern...' : 'Arbeitgeber anlegen'}
            </button>
          </div>
        </div>
      )}

      <div className="filters-bar">
        <div className="search-wrapper">
          <span className="search-icon">🔍</span>
          <input type="text" placeholder="Arbeitgeber suchen..." value={search} onChange={(e) => setSearch(e.target.value)} className="search-input" id="ag-search" />
        </div>
      </div>

      <div className="table-card">
        {loading ? (
          <div className="table-loading"><div className="loading-spinner" /></div>
        ) : filtered.length === 0 ? (
          <div className="empty-state"><span className="empty-icon">🏢</span><p>Keine Arbeitgeber gefunden.</p></div>
        ) : (
          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Typ</th>
                  <th>Träger</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((a) => (
                  <tr key={a.id} onClick={() => navigate(`/admin/arbeitgeber/${a.id}`)}>
                    <td className="cell-name"><span>{a.name}</span>{a.website && <span className="cell-sub">{a.website}</span>}</td>
                    <td>{EINRICHTUNGSTYPEN.find((t) => t.value === a.einrichtungstyp)?.label || a.einrichtungstyp || '–'}</td>
                    <td>{a.traeger || '–'}</td>
                    <td><span className="status-badge" style={{ '--badge-color': a.status === 'aktiv' ? '#10B981' : '#94A3B8' } as React.CSSProperties}>{a.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
